import React from "react"
import styled from "styled-components"
import { dark } from "../../colors"
import { Location } from "../types"
import Radar from "radar-sdk-js"
import { debounce } from "lodash"
import axios from "axios"

interface ChangeLocationProps {
  radarPublishableKey: string
  location: Location
  loadingImageUrl: string
  redirectPath: string
}

export default function ChangeLocation({
  radarPublishableKey,
  location,
  loadingImageUrl,
  redirectPath
}: ChangeLocationProps) {
  function currentFilter() {
    const fromFinder = document.getElementById("community-finder")?.dataset.communityFinderFilterValue
    const fromQuery = new URLSearchParams(window.location.search).get("filter")
    return [fromFinder, fromQuery].find((value) => value && value !== "null" && value !== "undefined")
  }

  function redirectUrl() {
    const url = new URL(redirectPath, window.location.origin)
    const filter = currentFilter()
    if (filter) {
      url.searchParams.set("filter", filter)
    }
    return `${url.pathname}${url.search}`
  }

  function close() {
    const changeLocation = document.getElementById("community-finder-change-location")
    if (changeLocation) {
      changeLocation.classList.add("hidden")
    }

    const list = document.getElementById("community-finder-list")
    if (list) {
      list.classList.remove("hidden")
    }
  }

  const [searchTerm, setSearchTerm] = React.useState<string>("")
  const [loadingCurrentLocation, setLoadingCurrentLocation] = React.useState<boolean>(false)
  const [locations, setLocations] = React.useState<Array<Location>>([])
  const [error, setError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState<boolean>(false)

  React.useEffect(() => {
    Radar.initialize(radarPublishableKey)
  }, [])

  async function saveAndUpdateLocation(location: Location) {
    setError(null)
    setSaving(true)
    try {
      const response = await axios.put("/api/front_end/current_location", location)

      // Verify the response indicates success and contains location data
      if (!response.data?.location) {
        setError("Failed to save location. Please try again.")
        setSaving(false)
        return
      }

      // Use replace instead of href to avoid adding to browser history
      window.location.replace(redirectUrl())
    } catch (e) {
      console.error("Error saving location", e)
      setError("Unable to save location. Please check your connection and try again.")
      setSaving(false)
    }
  }

  async function getCurrentLocation() {
    setError(null)
    setLoadingCurrentLocation(true)
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log("Geocoding position.coords", position.coords)

        const result = await Radar.reverseGeocode(position.coords)
        console.log("Radar reverseGeocode result", result)
        if (result && result.addresses && result.addresses.length > 0) {
          const { city, stateCode, postalCode, countryCode } = result.addresses[0]
          await saveAndUpdateLocation({
            name: `${city}, ${stateCode}`,
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            city: city,
            state: stateCode,
            zip: postalCode,
            countryCode: countryCode,
            source: "geolocation",
            accuracy: position.coords.accuracy
          })
        } else {
          setError("Could not determine your location. Please try searching for a city instead.")
        }
        setLoadingCurrentLocation(false)
      },
      (error) => {
        setLoadingCurrentLocation(false)
        console.error(error)
        setError("Unable to access your location. Please allow location access or search for a city instead.")
      }
    )
  }

  const getAutocompleteResults = React.useCallback(async (query) => {
    if (query) {
      const result = await Radar.autocomplete({
        query,
        near: {
          latitude: location.lat,
          longitude: location.lng
        },
        layers: ["locality"],
        countryCode: "US,CA"
      })
      console.log(`Radar autocomplete results for ${query}`, result?.addresses)
      if (result && result.addresses && result.addresses.length > 0) {
        setLocations(
          result.addresses.map((a) => {
            const { latitude, longitude, city, stateCode, postalCode, countryCode } = a
            return {
              name: `${city}, ${stateCode}`,
              lat: latitude,
              lng: longitude,
              city: city,
              state: stateCode,
              zip: postalCode,
              countryCode: countryCode,
              source: "search"
            }
          })
        )
        return
      }
    }
    setLocations([])
  }, [])

  const debouncedGetAutocompleteResults = React.useMemo(() => {
    return debounce(getAutocompleteResults, 500)
  }, [])

  function handleSearchTermChange(e) {
    const { value } = e.target
    setSearchTerm(value)
    void debouncedGetAutocompleteResults(value)
  }

  let rightAction = (
    <a onClick={getCurrentLocation}>
      <i className="fa-regular fa-location-arrow"></i>
    </a>
  )

  if (loadingCurrentLocation) {
    rightAction = <img src={loadingImageUrl} alt="Loading" />
  } else if (searchTerm) {
    rightAction = (
      <a onClick={() => setSearchTerm("")}>
        <i className="fa-solid fa-circle-xmark"></i>
      </a>
    )
  }

  return (
    <Container>
      <InputContainer>
        <LeftAction>
          <a onClick={close}>
            <i className="fa-solid fa-arrow-left" />
          </a>
        </LeftAction>
        <Input value={searchTerm} onChange={handleSearchTermChange} placeholder="Search by city, state" />
        <RightAction>{rightAction}</RightAction>
      </InputContainer>
      {error && (
        <ErrorMessage>
          {error}
          <button onClick={() => setError(null)} aria-label="Dismiss error">
            <i className="fa-solid fa-circle-xmark"></i>
          </button>
        </ErrorMessage>
      )}
      {locations.length > 0 && (
        <Results>
          {locations.map((l, i) => (
            <Result key={i}>
              <a
                onClick={() => !saving && saveAndUpdateLocation(l)}
                style={{ opacity: saving ? 0.6 : 1, pointerEvents: saving ? "none" : "auto" }}
              >
                <div>{l.name}</div>
                {saving ? (
                  <img src={loadingImageUrl} alt="Saving" style={{ width: "1rem", height: "1rem" }} />
                ) : (
                  <i className="fa-light fa-angle-right"></i>
                )}
              </a>
            </Result>
          ))}
        </Results>
      )}
    </Container>
  )
}

const Container = styled.div`
  height: calc(100vh - 90px);
`

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid ${dark};
  border-radius: 0.5rem;
  padding: 0 0.5rem;
  background-color: white;
`

const Input = styled.input`
  display: flex;
  flex: 1;
  border: none;
  background-color: white;
  font-family: "Poppins", sans-serif;
  font-size: 1rem;
  height: 48px;
  outline: none;
`

const LeftAction = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 4px;
  width: 36px;
`

const RightAction = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 4px;
  width: 36px;

  img {
    width: 2rem;
    height: 2rem;
  }
`

const Results = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-top: 1rem;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem;
`

const Result = styled.div`
  padding: 12px;

  font-family: "Poppins", sans-serif;
  font-size: 1rem;
  line-height: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);

  a {
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`

const ErrorMessage = styled.div`
  margin-top: 1rem;
  padding: 12px;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 0.5rem;
  color: #c33;
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  button {
    background: none;
    border: none;
    color: #c33;
    cursor: pointer;
    padding: 0;
    margin-left: 8px;
    display: flex;
    align-items: center;
  }
`
